import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import type {
	ProethicaNextEntryChip,
	ProethicaNextEntryMeta,
} from "@features/report/helpers/proethica-next-entry";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, type IconType, TextSkeleton, useDevice } from "@memorang/ui";
import { ProethicaTestBadge } from "components/ProethicaTestBadge";
import { Image } from "expo-image";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button, Card, Text } from "react-native-paper";

const CHIP_ICONS: Record<ProethicaNextEntryChip["kind"], IconType> = {
	duration: "clock-outline",
	activities: "check-circle-outline",
	knowledgeCheck: "clipboard-check-outline",
	questions: "help-circle-outline",
};

const chipLabel = (chip: ProethicaNextEntryChip, t: TFunction): string =>
	chip.kind === "duration"
		? chip.label
		: t(`report.proethica.nextEntry.${chip.kind}`, { count: chip.count });

const EntryChip = ({ chip }: { chip: ProethicaNextEntryChip }) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	return (
		<Box
			flexDirection="row"
			alignItems="center"
			paddingHorizontal={12}
			paddingVertical={6}
			borderRadius={8}
			backgroundColor={theme.colors.primaryContainer}
			style={{ gap: 6 }}
		>
			<Icon
				name={CHIP_ICONS[chip.kind]}
				size={14}
				color={theme.colors.onSurfaceVariant}
				accessible={false}
			/>
			<Text
				variant="labelMedium"
				style={{ color: theme.colors.onSurfaceVariant }}
			>
				{chipLabel(chip, t)}
			</Text>
		</Box>
	);
};

const EntryThumbnail = ({ meta }: { meta: ProethicaNextEntryMeta }) => {
	const theme = useAppTheme();
	if (meta.testKind) {
		return <ProethicaTestBadge kind={meta.testKind} size={64} />;
	}
	if (!meta.thumbnailSource) {
		return null;
	}
	return (
		<Image
			source={meta.thumbnailSource}
			style={{ width: 64, height: 64, borderRadius: theme.roundness }}
			contentFit="cover"
			alt=""
		/>
	);
};

// `meta` is absent only while the learning path is still resolving, when the
// Continue CTA stays present but disabled.
export const NextEntryCard = ({
	meta,
	continueLabel,
	isContinueLoading,
	handleContinue,
}: {
	meta?: ProethicaNextEntryMeta;
	continueLabel: string;
	isContinueLoading: boolean;
	handleContinue: () => void;
}) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const continueButton = (
		<Button
			mode="contained"
			loading={isContinueLoading}
			disabled={isContinueLoading}
			onPress={handleContinue}
			contentStyle={{ height: 48 }}
			style={isMobile ? undefined : { minWidth: 200 }}
		>
			{continueLabel}
		</Button>
	);

	return (
		<Card
			mode="contained"
			style={{ backgroundColor: theme.colors.surfaceVariant }}
		>
			<Box
				padding={20}
				flexDirection={isMobile ? "column" : "row"}
				alignItems={isMobile ? "stretch" : "center"}
				style={{ gap: 16 }}
			>
				{meta ? <EntryThumbnail meta={meta} /> : null}
				<Box flex={isMobile ? undefined : 1} style={{ gap: 8 }}>
					{meta ? (
						<>
							{meta.chips.length ? (
								<Box flexDirection="row" flexWrap="wrap" style={{ gap: 8 }}>
									{meta.chips.map((chip) => (
										<EntryChip key={chip.kind} chip={chip} />
									))}
								</Box>
							) : null}
							<Text
								variant="titleMedium"
								style={{ color: theme.colors.onSurface, fontWeight: "800" }}
							>
								{meta.title}
							</Text>
							{meta.description ? (
								<Text
									variant="bodyMedium"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{meta.description}
								</Text>
							) : null}
						</>
					) : (
						<TextSkeleton numLines={2} height={18} />
					)}
				</Box>
				{continueButton}
			</Box>
		</Card>
	);
};
